import React from "react";
import type { GetStaticProps } from "next";
import Link from "next/link";
import SiteShell, { APP_URL } from "@/components/SiteShell";
import FoundingMemberBanner from "@/components/FoundingMemberBanner";
import { TEMPLATES } from "@/templates";

/**
 * Counts come from the registries at build time rather than being typed into
 * the copy — the homepage claimed "six proven funnels" for three weeks after
 * the gallery reached 26. Read in getStaticProps so the template flow
 * definitions never reach the client bundle.
 */
export const getStaticProps: GetStaticProps<{ templateCount: number }> = () => ({
  props: { templateCount: TEMPLATES.length },
});

/** Marketing landing page (served at www.frazzlecrm.com/). */
export default function SiteHome({ templateCount }: { templateCount: number }) {
  return (
    <SiteShell title="FrazzleCRM — The map IS the automation">
      <FoundingMemberBanner />
      {/* hero */}
      <section className="bg-gradient-to-b from-indigo-50/60 to-white">
        <div className="mx-auto max-w-6xl px-6 pb-20 pt-20 text-center">
          <div className="mx-auto mb-4 w-fit rounded-full border border-indigo-200 bg-indigo-50 px-3 py-1 text-[12px] font-semibold text-indigo-700">
            Don&apos;t get Frazzled… Get FrazzleCRM
          </div>
          <h1 className="mx-auto max-w-3xl text-[44px] font-extrabold leading-[1.1] tracking-tight text-slate-900 sm:text-[56px]">
            Map it.{" "}
            <span className="bg-gradient-to-r from-indigo-600 to-violet-600 bg-clip-text text-transparent">
              Automate it.
            </span>
          </h1>
          <p className="mx-auto mt-5 max-w-2xl text-[17px] leading-relaxed text-slate-600">
            In every other tool, your funnel diagram and your automations
            drift apart until nobody knows what actually fires. In FrazzleCRM
            they are the same thing: draw the flow on the map and it runs for
            real — edit either side, both change.
          </p>
          <div className="mt-8 flex items-center justify-center gap-3">
            <a
              href={`${APP_URL}/signup`}
              className="rounded-xl bg-indigo-600 px-6 py-3 text-[15px] font-semibold text-white shadow-lg shadow-indigo-200 hover:bg-indigo-500"
            >
              Start free — no credit card
            </a>
            <Link
              href="/pricing"
              className="rounded-xl border border-slate-200 bg-white px-6 py-3 text-[15px] font-semibold text-slate-700 hover:bg-slate-50"
            >
              See pricing
            </Link>
          </div>
          <p className="mt-3 text-[12.5px] text-slate-400">
            Free forever for 20 flows, 1 map and 500 contacts.
          </p>
        </div>
      </section>

      {/* feature trio */}
      <section className="mx-auto max-w-6xl px-6 py-16">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {[
            {
              title: "One canvas, real automations",
              body: "Flows on the map are live automations — triggers, waits, emails, SMS, branches, goals. Draw an edge and it's wired. The Traffic view animates how contacts actually move between flows, tags and providers.",
              icon: "🗺",
            },
            {
              title: "Email done properly",
              body: "A full compose surface with templates, senders, from-name control, preheaders, merge fields and test sends — on the map and in the builder. Broken links in any message get flagged automatically, every 10 minutes.",
              icon: "✉️",
            },
            {
              title: "AI is a first-class citizen",
              body: "Connect Claude, ChatGPT or any MCP-capable AI to your workspace. It can explain your funnels, audit automations for conflicts, draft new flows, and leave annotations right on the map.",
              icon: "🤖",
            },
          ].map((f) => (
            <div
              key={f.title}
              className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm"
            >
              <div className="text-[28px]">{f.icon}</div>
              <h3 className="mt-3 text-[17px] font-bold text-slate-900">
                {f.title}
              </h3>
              <p className="mt-2 text-[14px] leading-relaxed text-slate-600">
                {f.body}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* everything list */}
      <section className="border-y border-slate-100 bg-slate-50/60">
        <div className="mx-auto max-w-6xl px-6 py-14">
          <h2 className="text-center text-[26px] font-bold text-slate-900">
            Everything a funnel actually needs
          </h2>
          <div className="mx-auto mt-8 grid max-w-4xl grid-cols-2 gap-x-8 gap-y-3 text-[14px] text-slate-600 sm:grid-cols-3">
            {[
              "Visual funnel mapper",
              "Two-way map ⟷ automation sync",
              "Flexible waits (dates, fields, events)",
              "Appointments & reminders",
              "Sales Scripts with live call tracking",
              "Tag & field triggers",
              "Send windows & quiet hours",
              "Goals that skip contacts ahead",
              "A/B split testing",
              "Email templates & test sends",
              "Broken-link watchdog",
              "Contact import / export",
              "Contact relationships",
              "Multi-workspace teams",
              "Inbound & outbound webhooks",
              "Public API (Pro)",
              "MCP server for your AI (Pro)",
            ].map((f) => (
              <div key={f} className="flex items-center gap-2">
                <span className="text-emerald-500">✓</span> {f}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* free tools + templates */}
      <section className="mx-auto max-w-6xl px-6 py-16">
        <h2 className="text-center text-[26px] font-bold text-slate-900">
          Try it before you even sign up
        </h2>
        <div className="mx-auto mt-8 grid max-w-4xl grid-cols-1 gap-6 md:grid-cols-3">
          {[
            {
              href: "/tools/funnel-generator",
              icon: "🗺",
              title: "Funnel Map Generator",
              body: "Describe your business in a sentence — get a complete funnel map you can claim into a free workspace.",
            },
            {
              href: "/tools/email-link-checker",
              icon: "🔗",
              title: "Email Link Checker",
              body: "Paste your email HTML and catch broken links before your subscribers do. Free, instant.",
            },
            {
              href: "/templates",
              icon: "📦",
              title: "Template gallery",
              body: `${templateCount} proven funnels — onboarding, cart recovery, webinars — pre-drawn, pre-wired, one click to clone.`,
            },
          ].map((t) => (
            <Link
              key={t.href}
              href={t.href}
              className="group rounded-2xl border border-slate-100 bg-white p-6 shadow-sm transition-shadow hover:shadow-md"
            >
              <div className="text-[28px]">{t.icon}</div>
              <h3 className="mt-3 text-[16px] font-bold text-slate-900 group-hover:text-indigo-700">
                {t.title}
              </h3>
              <p className="mt-2 text-[13.5px] leading-relaxed text-slate-600">
                {t.body}
              </p>
              <span className="mt-3 inline-block text-[13px] font-semibold text-indigo-600">
                Try it →
              </span>
            </Link>
          ))}
        </div>
        <p className="mt-8 text-center text-[13.5px] text-slate-500">
          Selling on the phone?{" "}
          <Link
            href="/sales-scripts"
            className="font-medium text-indigo-600 hover:text-indigo-500"
          >
            Sales Scripts
          </Link>{" "}
          put the call itself on the map — a branching script your reps click
          through, and a record of the route every call took.
        </p>
        <p className="mt-3 text-center text-[13.5px] text-slate-500">
          New to this?{" "}
          <Link
            href="/learn"
            className="font-medium text-indigo-600 hover:text-indigo-500"
          >
            The automation glossary
          </Link>{" "}
          defines every moving part — triggers, waits, goals, stop tags — in
          plain English, with the mistakes each one causes.
        </p>
      </section>

      {/* who it's for */}
      <section className="border-t border-slate-100 bg-slate-50/60">
        <div className="mx-auto max-w-6xl px-6 py-12 text-center">
          <h2 className="text-[22px] font-bold text-slate-900">
            Built for how you actually sell
          </h2>
          <div className="mx-auto mt-6 flex max-w-3xl flex-wrap justify-center gap-2.5">
            {[
              ["/for/agencies", "Agencies"],
              ["/for/coaches", "Coaches & consultants"],
              ["/for/ecommerce", "E-commerce"],
              ["/for/saas", "SaaS"],
              ["/for/course-creators", "Course creators"],
              ["/for/local-services", "Local services"],
            ].map(([href, label]) => (
              <Link
                key={href}
                href={href}
                className="rounded-full border border-slate-200 bg-white px-4 py-2 text-[13.5px] font-semibold text-slate-700 shadow-sm hover:border-indigo-300 hover:text-indigo-700"
              >
                {label}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* building in public */}
      <section className="mx-auto max-w-4xl px-6 py-12 text-center">
        <div className="rounded-2xl border border-indigo-100 bg-indigo-50/40 px-6 py-8">
          <div className="mx-auto mb-3 w-fit rounded-full border border-indigo-200 bg-white px-3 py-1 text-[12px] font-semibold text-indigo-700">
            Building in public
          </div>
          <p className="mx-auto max-w-2xl text-[15px] leading-relaxed text-slate-700">
            We pointed an AI agent at our own CRM and let it run growth — pages,
            free tools, deploys, and all. Every number is public, including the
            ones we&apos;d rather hide.{" "}
            <Link
              href="/building-in-public"
              className="font-semibold text-indigo-600 hover:text-indigo-500"
            >
              See the real metrics →
            </Link>
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-6xl px-6 py-20 text-center">
        <h2 className="text-[30px] font-bold text-slate-900">
          Don&apos;t get Frazzled… Get FrazzleCRM.
        </h2>
        <p className="mx-auto mt-3 max-w-xl text-[15px] text-slate-600">
          Start mapping in minutes. Upgrade only when you outgrow the free
          tier.
        </p>
        <a
          href={`${APP_URL}/signup`}
          className="mt-7 inline-block rounded-xl bg-indigo-600 px-7 py-3.5 text-[15px] font-semibold text-white shadow-lg shadow-indigo-200 hover:bg-indigo-500"
        >
          Start free — no credit card
        </a>
      </section>
    </SiteShell>
  );
}
